import React from "react";

type StackedBarChartData = {
  name: string;
  datasets: {
    label: string;
    value: number;
    color: string;
  }[];
};

export const NativeBarChart = ({
  data,
  formatCurrency,
  height = 300,
}: {
  data: StackedBarChartData[];
  formatCurrency: (v: number) => string;
  height?: number;
}) => {
  // Find the maximum total value across all stacks to scale properly
  const maxTotal = Math.max(
    ...data.map((d) => d.datasets.reduce((sum, set) => sum + set.value, 0)),
  );

  // Calculate sensible Y-axis ticks
  const ticks = [maxTotal, maxTotal * 0.75, maxTotal * 0.5, maxTotal * 0.25, 0];

  return (
    <div className="w-full flex-col font-sans relative" style={{ height }}>
      {/* Chart Area */}
      <div className="flex-1 flex h-full ml-12 relative border-b border-border pl-4">
        {/* Y Axis Labels (Absolute positioning outside the flex box) */}
        <div className="absolute top-0 bottom-0 -left-14 w-12 flex flex-col justify-between text-[10px] text-muted-foreground text-right pr-2">
          {ticks.map((tick, i) => (
            <span key={i} className="-mt-1.5">
              {formatCurrency(tick)}
            </span>
          ))}
        </div>

        {/* Horizontal Grid Lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none z-0">
          {ticks.map((_, i) => (
            <div key={i} className="w-full h-[1px] bg-border/40"></div>
          ))}
        </div>

        {/* Bars Container */}
        <div className="h-full w-full flex justify-around items-end pt-12 z-10">
          {data.map((col, idx) => {
            const totalVal = col.datasets.reduce(
              (sum, set) => sum + set.value,
              0,
            );
            const heightPct = maxTotal > 0 ? (totalVal / maxTotal) * 100 : 0;

            return (
              <div
                key={idx}
                className="flex flex-col items-center group relative w-full px-4 sm:px-12 md:px-24 h-full justify-end"
              >
                {/* Custom Tooltip */}
                <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none bg-popover border border-border shadow-xl rounded-lg p-3 z-50 min-w-[220px]">
                  <p className="text-xs font-bold border-b border-border/50 pb-1.5 mb-2">
                    {col.name}
                  </p>
                  {col.datasets.map((set, sidx) => (
                    <div
                      key={sidx}
                      className="flex justify-between items-center text-xs py-0.5"
                    >
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: set.color }}
                        ></div>
                        <span className="text-muted-foreground">
                          {set.label}
                        </span>
                      </div>
                      <span className="font-mono font-semibold">
                        {formatCurrency(set.value)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center text-xs py-1 border-t border-border/50 mt-1 font-bold">
                    <span>Total</span>
                    <span className="font-mono">
                      {formatCurrency(totalVal)}
                    </span>
                  </div>
                </div>

                {/* The Stacked Bar */}
                <div
                  className="w-full rounded-t-md overflow-hidden flex flex-col-reverse shadow-sm transition-all duration-500 ease-in-out hover:opacity-90"
                  style={{ height: `${heightPct}%` }}
                >
                  {col.datasets.map((set, sidx) => {
                    const segmentHeightPct =
                      totalVal > 0 ? (set.value / totalVal) * 100 : 0;
                    return (
                      <div
                        key={sidx}
                        className="w-full transition-all duration-500"
                        style={{
                          height: `${segmentHeightPct}%`,
                          backgroundColor: set.color,
                        }}
                      ></div>
                    );
                  })}
                </div>

                {/* X Axis Label */}
                <div className="absolute top-[100%] mt-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  {col.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="w-full flex justify-center items-center flex-wrap gap-x-6 gap-y-2 mt-12 mb-2">
        {data[0]?.datasets.map((set, i) => (
          <div
            key={i}
            className="flex items-center gap-x-2 text-xs text-muted-foreground"
          >
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: set.color }}
            ></div>
            {set.label}
          </div>
        ))}
      </div>
    </div>
  );
};
